import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { CATEGORIES } from "@/data/content";
import { useConfig } from "@/context/ConfigContext";
import { waLink } from "@/config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border-b border-darknavy/25 bg-transparent py-3 text-base text-darknavy placeholder:text-darknavy/40 outline-none transition-colors duration-300 focus:border-aqua";

export function EnquiryForm({ defaultCategory = "", compact = false }) {
  const cfg = useConfig();
  const [form, setForm] = useState({ name: "", phone: "", email: "", category: defaultCategory, message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, category: form.category || null, email: form.email || null });
      toast.success("Enquiry sent. We will get back to you shortly.");
      const cat = CATEGORIES.find((c) => c.slug === form.category);
      const msg = `Hello Chhannulal & Brothers, I would like to enquire about ${cat ? cat.name : "your products"}. My name is ${form.name}. ${form.message}`;
      window.open(waLink(cfg, msg), "_blank", "noopener");
      setForm({ name: "", phone: "", email: "", category: defaultCategory, message: "" });
    } catch (err) {
      toast.error("Could not send your enquiry. Please try WhatsApp or call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-7" data-testid="enquiry-form">
      <div className={compact ? "space-y-7" : "grid gap-7 md:grid-cols-2"}>
        <div>
          <label htmlFor="enq-name" className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-navy">Name *</label>
          <input id="enq-name" data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
        </div>
        <div>
          <label htmlFor="enq-phone" className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-navy">Phone Number *</label>
          <input id="enq-phone" data-testid="enquiry-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="Your phone number" className={inputCls} />
        </div>
      </div>
      <div className={compact ? "space-y-7" : "grid gap-7 md:grid-cols-2"}>
        <div>
          <label htmlFor="enq-email" className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-navy">Email</label>
          <input id="enq-email" data-testid="enquiry-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
        </div>
        <div>
          <label htmlFor="enq-category" className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-navy">Product Category</label>
          <select id="enq-category" data-testid="enquiry-category-select" value={form.category} onChange={set("category")} className={`${inputCls} cursor-pointer`}>
            <option value="">Select a category</option>
            {CATEGORIES.map((c) => (
              <option key={c.slug} value={c.slug}>{c.name}</option>
            ))}
            <option value="other">Other / Multiple</option>
          </select>
        </div>
      </div>
      <div>
        <label htmlFor="enq-message" className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-navy">Requirement / Message *</label>
        <textarea id="enq-message" data-testid="enquiry-message-input" required rows={4} value={form.message} onChange={set("message")} placeholder="Tell us what you're building or looking for" className={`${inputCls} resize-none`} />
      </div>
      <button
        type="submit"
        disabled={sending}
        data-testid="enquiry-submit-button"
        className="group flex items-center gap-3 bg-navy px-8 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-aqua disabled:opacity-60"
      >
        {sending ? "Sending…" : "Send Enquiry"}
        <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </form>
  );
}
